// The print window's serial card: a small studio slip for the customer's photo packet — the shop's name (and logo) on a
// coloured header, the photo, its serial (the photo's file name) standing out in a pill, the date, and the shop's address
// and phone in the footer. Drawn once at 600 DPI into a PNG, so the sheet, the print and the PDF all show the same image.

export const SHOP_CARD_MM = { w: 36, h: 52 };

const DPI = 600; // twice print resolution, so a card dragged up to double size still prints sharp
const PX = DPI / 25.4; // px per mm
const FONT = "'Hind Siliguri','Noto Sans Bengali',sans-serif";
const BRAND = '#E68A00';
const BRAND_LIGHT = '#F6A623';
const INK = '#1f2937';
const MUTED = '#6b7280';

const loadImg = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
});

function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

// Split text into at most `maxLines` lines that fit `maxW` (the last one ends in … when it still doesn't fit).
function wrapLines(ctx, text, maxW, maxLines) {
    const words = String(text || '').trim().split(/\s+/).filter(Boolean);
    const lines = [];
    let line = '';
    for (const word of words) {
        const next = line ? `${line} ${word}` : word;
        if (ctx.measureText(next).width <= maxW || !line) { line = next; continue; }
        lines.push(line);
        line = word;
        if (lines.length === maxLines) break;
    }
    if (line && lines.length < maxLines) lines.push(line);
    const used = lines.join(' ').split(/\s+/).length;
    if (used < words.length && lines.length) {
        let last = lines[lines.length - 1];
        while (last && ctx.measureText(`${last}…`).width > maxW) last = last.slice(0, -1);
        lines[lines.length - 1] = `${last}…`;
    }
    return lines;
}

// The largest font size (mm, from `max` down to `min`) at which `text` fits in `maxLines` lines.
function fitText(ctx, text, weight, maxW, maxLines, max, min) {
    for (let size = max; size >= min; size -= 0.2) {
        ctx.font = `${weight} ${size * PX}px ${FONT}`;
        const lines = wrapLines(ctx, text, maxW, maxLines + 1);
        if (lines.length <= maxLines && !lines.some((l) => l.endsWith('…'))) return { size, lines };
    }
    ctx.font = `${weight} ${min * PX}px ${FONT}`;
    return { size: min, lines: wrapLines(ctx, text, maxW, maxLines) };
}

// object-fit: cover, anchored to the top (a passport photo keeps the head).
function drawCoverTop(ctx, img, x, y, w, h) {
    const ir = img.naturalWidth / img.naturalHeight;
    const r = w / h;
    let sx = 0, sy = 0, sw = img.naturalWidth, sh = img.naturalHeight;
    if (ir > r) { sw = sh * r; sx = (img.naturalWidth - sw) / 2; } else { sh = sw / r; }
    ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

/**
 * @param shop      { name, address, phone, logo }  — `logo` a same-origin data URL or null
 * @param photo     same-origin data/blob URL of the photo, or null
 * @param serial    the slip's serial text — the photo's file name
 * @param date      the date text to show
 * @param labels    { phone, date } in the UI language
 * @returns a PNG data URL of the card
 */
export async function buildShopCard({ shop, photo, serial = '', date, labels }) {
    try { await document.fonts?.load(`700 40px ${FONT}`); } catch { /* the card still draws with the fallback font */ }
    const W = Math.round(SHOP_CARD_MM.w * PX);
    const H = Math.round(SHOP_CARD_MM.h * PX);
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    const mm = (v) => v * PX;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    // Card
    ctx.fillStyle = '#ffffff';
    roundRect(ctx, 0, 0, W, H, mm(2));
    ctx.fill();
    ctx.save();
    roundRect(ctx, 0, 0, W, H, mm(2));
    ctx.clip();

    // Header band — the shop's name (and logo)
    const headH = mm(10);
    const grad = ctx.createLinearGradient(0, 0, W, headH);
    grad.addColorStop(0, BRAND);
    grad.addColorStop(1, BRAND_LIGHT);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, headH);
    // a soft curve under the band
    ctx.beginPath();
    ctx.moveTo(0, headH);
    ctx.quadraticCurveTo(W / 2, headH + mm(2.6), W, headH);
    ctx.lineTo(W, headH - 1);
    ctx.lineTo(0, headH - 1);
    ctx.closePath();
    ctx.fill();

    let logoW = 0;
    if (shop.logo) {
        try {
            const logo = await loadImg(shop.logo);
            const d = mm(6.4);
            const lx = mm(2);
            const ly = (headH - d) / 2;
            ctx.save();
            ctx.beginPath();
            ctx.arc(lx + d / 2, ly + d / 2, d / 2, 0, Math.PI * 2);
            ctx.fillStyle = '#ffffff';
            ctx.fill();
            ctx.clip();
            const k = Math.min((d * 0.82) / logo.naturalWidth, (d * 0.82) / logo.naturalHeight);
            ctx.drawImage(logo, lx + (d - logo.naturalWidth * k) / 2, ly + (d - logo.naturalHeight * k) / 2, logo.naturalWidth * k, logo.naturalHeight * k);
            ctx.restore();
            logoW = d + mm(1);
        } catch { /* no logo */ }
    }
    const nameX0 = mm(2) + logoW;
    const nameW = W - nameX0 - mm(2);
    const nameFit = fitText(ctx, shop.name || '', 700, nameW, 2, 3.3, 1.9);
    ctx.fillStyle = '#ffffff';
    const nameLineH = mm(nameFit.size * 1.2);
    const nameTop = (headH - nameLineH * nameFit.lines.length) / 2 + mm(nameFit.size) * 0.92;
    nameFit.lines.forEach((l, i) => ctx.fillText(l, nameX0 + nameW / 2, nameTop + i * nameLineH));

    // Customer photo in a white frame with a soft shadow
    const pw = mm(14.4);
    const ph = mm(17.6);
    const px = (W - pw) / 2;
    const py = mm(12.6);
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.18)';
    ctx.shadowBlur = mm(1);
    ctx.shadowOffsetY = mm(0.3);
    ctx.fillStyle = '#ffffff';
    roundRect(ctx, px - mm(0.6), py - mm(0.6), pw + mm(1.2), ph + mm(1.2), mm(0.9));
    ctx.fill();
    ctx.restore();
    if (photo) {
        try {
            const img = await loadImg(photo);
            ctx.save();
            roundRect(ctx, px, py, pw, ph, mm(0.4));
            ctx.clip();
            if (img.naturalWidth > img.naturalHeight) {
                // a wide photo (a dual) is shown whole, centred in the frame, instead of cutting the people at the sides
                const k = Math.min(pw / img.naturalWidth, ph / img.naturalHeight);
                ctx.drawImage(img, px + (pw - img.naturalWidth * k) / 2, py + (ph - img.naturalHeight * k) / 2, img.naturalWidth * k, img.naturalHeight * k);
            } else {
                drawCoverTop(ctx, img, px, py, pw, ph);
            }
            ctx.restore();
        } catch { /* empty frame */ }
    }

    // Footer band (drawn below), and between it and the photo: the serial in a pill, then the date — centred as one block.
    // The serial stays on one line across nearly the card's full width, as big as fits (3.8mm down to 1.9mm); only a very
    // long name falls to two lines.
    const footH = mm(8.6);
    const footY = H - footH;
    const maxTextW = W - mm(4.4);
    let size = 3.8;
    let lines = serial ? [serial] : [];
    ctx.font = `700 ${mm(size)}px ${FONT}`;
    while (serial && size > 1.9 && ctx.measureText(serial).width > maxTextW) {
        size -= 0.2;
        ctx.font = `700 ${mm(size)}px ${FONT}`;
    }
    if (serial && ctx.measureText(serial).width > maxTextW) {
        const fit = fitText(ctx, serial, 700, maxTextW, 2, 2.4, 1.7);
        size = fit.size;
        lines = fit.lines;
        ctx.font = `700 ${mm(size)}px ${FONT}`;
    }
    const lineH = mm(size * 1.2);
    const pillH = lines.length ? lineH * lines.length + mm(1.6) : 0;
    const dateH = mm(2.4);
    const blockH = pillH + (pillH ? mm(0.6) : 0) + dateH;
    let y = py + ph + mm(0.6) + (footY - (py + ph + mm(0.6)) - blockH) / 2;
    if (lines.length) {
        const textW = Math.max(...lines.map((l) => ctx.measureText(l).width));
        const tw = Math.min(textW + mm(3), W - mm(1.6));
        ctx.fillStyle = '#FFF1DC';
        roundRect(ctx, (W - tw) / 2, y, tw, pillH, Math.min(pillH / 2, mm(2.7)));
        ctx.fill();
        ctx.strokeStyle = BRAND;
        ctx.lineWidth = mm(0.3);
        ctx.stroke();
        ctx.fillStyle = BRAND;
        lines.forEach((l, k) => ctx.fillText(l, W / 2, y + mm(0.8) + lineH * k + mm(size) * 0.95, tw - mm(1.4)));
        y += pillH + mm(0.6);
    }
    ctx.font = `500 ${mm(1.7)}px ${FONT}`;
    ctx.fillStyle = MUTED;
    ctx.fillText(`${labels.date}: ${date}`, W / 2, y + mm(1.7));

    // Footer — the shop's address and phone
    ctx.fillStyle = '#FFF4E0';
    ctx.fillRect(0, footY, W, footH);
    ctx.fillStyle = BRAND;
    ctx.fillRect(0, footY, W, mm(0.4));
    // the address (up to 2 lines) and the phone, centred in the band as one block
    const addrFit = shop.address ? fitText(ctx, shop.address, 500, W - mm(4), 2, 1.8, 1.4) : null;
    const addrLH = addrFit ? mm(addrFit.size * 1.22) : 0;
    const phoneH = shop.phone ? mm(2.1) : 0;
    const gap = addrFit && shop.phone ? mm(0.7) : 0;
    const footBlockH = addrLH * (addrFit?.lines.length || 0) + gap + phoneH;
    let fy = footY + mm(0.4) + (footH - mm(0.4) - footBlockH) / 2;
    if (addrFit) {
        ctx.font = `500 ${mm(addrFit.size)}px ${FONT}`;
        ctx.fillStyle = INK;
        addrFit.lines.forEach((l, i) => ctx.fillText(l, W / 2, fy + mm(addrFit.size) * 0.9 + i * addrLH));
        fy += addrLH * addrFit.lines.length + gap;
    }
    if (shop.phone) {
        ctx.font = `700 ${mm(2.1)}px ${FONT}`;
        ctx.fillStyle = BRAND;
        ctx.fillText(`${labels.phone}: ${shop.phone}`, W / 2, fy + mm(2.1) * 0.9, W - mm(3));
    }
    ctx.restore();

    // Outer + inner frame lines
    ctx.strokeStyle = BRAND;
    ctx.lineWidth = mm(0.5);
    roundRect(ctx, mm(0.25), mm(0.25), W - mm(0.5), H - mm(0.5), mm(1.8));
    ctx.stroke();
    return canvas.toDataURL('image/png');
}
